import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { DocManiaLogoImage } from '../components/DocManiaLogo';
import { CreditBadge } from '../components/CreditBadge';
import { ProposalStepIndicator } from '../components/proposals/ProposalStepIndicator';
import { CurrencySelector } from '../components/ui/CurrencySelector';
import { getDocTypeDisplayName } from '../lib/docTypeDisplayNames';
import {
  COVER_LETTER_TEMPLATE_ID,
  COVER_LETTER_TEMPLATE_NAME,
} from '../lib/coverLetterRoutes';
import {
  scrollToResultCard,
  formatResultCardMeta,
  getDocumentDownloadLabel,
} from '../lib/documentGenerationUi';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Download, CheckCircle, Plus, Trash2, FileText,
  Upload, X, Image, Sparkles, Briefcase, GraduationCap, Award, Wrench, User, Mail, Users,
} from 'lucide-react';

const slugToBackendType = (slug) => slug.replace(/-/g, '_');

const PO_PAYMENT_TERM_PRESETS = [
  'Net 30',
  'Net 60',
  '50% advance / 50% on delivery',
  'Due on receipt',
  'Custom',
];

const LOGO_DOC_TYPES = ['invoice', 'quotation', 'proforma_invoice', 'delivery_note', 'waybill', 'purchase_order'];

/** Convert a data-URI (from GET /documents/{job_id}/logos) back into a File for re-upload. */
function dataURIToFile(dataURI, filename = 'restored-logo.png') {
  if (!dataURI || typeof dataURI !== 'string') return null;
  try {
    const [header, base64] = dataURI.split(',');
    const mime = (header.match(/data:(.+?);/) || [])[1] || 'image/png';
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const ext = (mime.split('/')[1] || 'png').split('+')[0];
    const name = filename.includes('.') ? filename : `${filename}.${ext}`;
    return new File([arr], name, { type: mime });
  } catch {
    return null;
  }
}

const TAX_LABEL_OPTIONS = ['VAT', 'Sales Tax', 'GST', 'HST', 'Custom'];

/** Currency → suggested tax label (user may override freely). */
function defaultTaxLabelForCurrency(currency) {
  const code = String(currency || 'USD').toUpperCase();
  if (['NGN', 'GBP', 'EUR', 'GHS', 'KES', 'ZAR'].includes(code)) return 'VAT';
  if (code === 'USD') return 'Sales Tax';
  if (code === 'CAD' || code === 'AUD') return 'GST';
  return 'VAT';
}

const COVER_LETTER_TONES = ['Professional', 'Confident', 'Warm'];

const RESUME_FLOW_STEPS = [
  { id: 1, title: 'Resume', hint: 'Your profile', icon: FileText },
  { id: 2, title: 'Cover Letter?', hint: 'Optional add-on', icon: Mail },
  { id: 3, title: 'Draft', hint: 'Review & edit', icon: Sparkles },
  { id: 4, title: 'Download', hint: 'Get your files', icon: Download },
];

const DEFAULT_COVER_CONTEXT = {
  jobTitle: '',
  companyName: '',
  jobDescription: '',
  tone: 'Professional',
};

/* ─── Default empty resume form state ─── */
const DEFAULT_RESUME = {
  fullName: '',
  professionalTitle: '',
  yearsOfExperience: '',
  email: '',
  phone: '',
  city: '',
  state: '',
  country: '',
  linkedinOrPortfolio: '',
  profileSummary: '',
  workExperience: [
    { position: '', company: '', jobCity: '', jobState: '', jobCountry: '', startDate: '', endDate: '', responsibilities: ['', '', ''] },
  ],
  education: [
    { degree: '', fieldOfStudy: '', institution: '', eduCity: '', eduState: '', eduCountry: '', graduationYear: '' },
  ],
  skills: ['', '', '', '', ''],
  certifications: [
    { certificationName: '', issuingBody: '', year: '' },
  ],
  references: [
    { referenceName: '', referenceTitle: '', referenceCompany: '', referencePhone: '', referenceEmail: '' },
  ],
};

/* ─── Map camelCase form → UPPERCASE template keys ─── */
function mapResumeToTemplateKeys(form) {
  return {
    FULL_NAME: form.fullName || '',
    PROFESSIONAL_TITLE: form.professionalTitle || '',
    YEARS_OF_EXPERIENCE: form.yearsOfExperience || '',
    EMAIL: form.email || '',
    PHONE: form.phone || '',
    CITY: form.city || '',
    STATE: form.state || '',
    COUNTRY: form.country || '',
    LINKEDIN_OR_PORTFOLIO: form.linkedinOrPortfolio || '',
    PROFILE_SUMMARY: form.profileSummary || '',
    WORK_EXPERIENCE: (form.workExperience || []).map(exp => ({
      POSITION: exp.position || '',
      COMPANY: exp.company || '',
      JOB_CITY: exp.jobCity || '',
      JOB_STATE: exp.jobState || '',
      JOB_COUNTRY: exp.jobCountry || '',
      START_DATE: exp.startDate || '',
      END_DATE: exp.endDate || '',
      RESPONSIBILITIES: (exp.responsibilities || []).filter(r => r.trim()),
    })),
    EDUCATION: (form.education || []).map(edu => ({
      DEGREE: edu.degree || '',
      FIELD_OF_STUDY: edu.fieldOfStudy || '',
      INSTITUTION: edu.institution || '',
      EDU_CITY: edu.eduCity || '',
      EDU_STATE: edu.eduState || '',
      EDU_COUNTRY: edu.eduCountry || '',
      GRADUATION_YEAR: edu.graduationYear || '',
    })),
    SKILLS: (form.skills || []).filter(s => s.trim()),
    CERTIFICATIONS: (form.certifications || []).map(c => ({
      CERTIFICATION_NAME: c.certificationName || '',
      ISSUING_BODY: c.issuingBody || '',
      YEAR: c.year || '',
    })).filter(c => c.CERTIFICATION_NAME),
    REFERENCES: (form.references || []).map(r => ({
      REFERENCE_NAME: r.referenceName || '',
      REFERENCE_TITLE: r.referenceTitle || '',
      REFERENCE_COMPANY: r.referenceCompany || '',
      REFERENCE_PHONE: r.referencePhone || '',
      REFERENCE_EMAIL: r.referenceEmail || '',
    })).filter(r => r.REFERENCE_NAME),
  };
}

const PAIRED_RESUME_JOB_KEY = '_PAIRED_RESUME_JOB_ID';

function mapCoverLetterPayload(resumeForm, context, draft, pairedResumeJobId = null) {
  return {
    ...mapResumeToTemplateKeys(resumeForm),
    COMPANY_NAME: context.companyName || '',
    JOB_TITLE: context.jobTitle || '',
    JOB_DESCRIPTION: context.jobDescription || '',
    TONE: context.tone || 'Professional',
    COVER_LETTER_BODY: draft || '',
    ...(pairedResumeJobId ? { [PAIRED_RESUME_JOB_KEY]: pairedResumeJobId } : {}),
  };
}

function splitCoverLetterInputData(data) {
  const pairedResumeJobId = data[PAIRED_RESUME_JOB_KEY] || data._paired_resume_job_id || null;
  return {
    resume: mapTemplateKeysToResume(data),
    cover: {
      jobTitle: data.JOB_TITLE || '',
      companyName: data.COMPANY_NAME || '',
      jobDescription: data.JOB_DESCRIPTION || '',
      tone: data.TONE || 'Professional',
      coverLetterBody: data.COVER_LETTER_BODY || '',
    },
    pairedResumeJobId,
  };
}

function isJobDownloadReady(job) {
  return job && (job.status === 'preview_ready' || job.status === 'completed');
}

/* ─── Reverse: UPPERCASE API data → camelCase form (for re-run) ─── */
function mapTemplateKeysToResume(data) {
  return {
    fullName: data.FULL_NAME || '',
    professionalTitle: data.PROFESSIONAL_TITLE || '',
    yearsOfExperience: data.YEARS_OF_EXPERIENCE || '',
    email: data.EMAIL || '',
    phone: data.PHONE || '',
    city: data.CITY || '',
    state: data.STATE || '',
    country: data.COUNTRY || '',
    linkedinOrPortfolio: data.LINKEDIN_OR_PORTFOLIO || '',
    profileSummary: data.PROFILE_SUMMARY || '',
    workExperience: (data.WORK_EXPERIENCE || []).map(exp => ({
      position: exp.POSITION || '',
      company: exp.COMPANY || '',
      jobCity: exp.JOB_CITY || '',
      jobState: exp.JOB_STATE || '',
      jobCountry: exp.JOB_COUNTRY || '',
      startDate: exp.START_DATE || '',
      endDate: exp.END_DATE || '',
      responsibilities: exp.RESPONSIBILITIES?.length ? exp.RESPONSIBILITIES : ['', '', ''],
    })),
    education: (data.EDUCATION || []).map(edu => ({
      degree: edu.DEGREE || '',
      fieldOfStudy: edu.FIELD_OF_STUDY || '',
      institution: edu.INSTITUTION || '',
      eduCity: edu.EDU_CITY || '',
      eduState: edu.EDU_STATE || '',
      eduCountry: edu.EDU_COUNTRY || '',
      graduationYear: edu.GRADUATION_YEAR || '',
    })),
    skills: data.SKILLS?.length ? data.SKILLS : ['', '', '', '', ''],
    certifications: (data.CERTIFICATIONS || []).map(c => ({
      certificationName: c.CERTIFICATION_NAME || '',
      issuingBody: c.ISSUING_BODY || '',
      year: c.YEAR || '',
    })),
    references: (data.REFERENCES || []).length
      ? (data.REFERENCES || []).map(r => ({
          referenceName: r.REFERENCE_NAME || '',
          referenceTitle: r.REFERENCE_TITLE || '',
          referenceCompany: r.REFERENCE_COMPANY || '',
          referencePhone: r.REFERENCE_PHONE || '',
          referenceEmail: r.REFERENCE_EMAIL || '',
        }))
      : [{ referenceName: '', referenceTitle: '', referenceCompany: '', referencePhone: '', referenceEmail: '' }],
  };
}

/* ═══════════════════════════════════════════════════════
   RESUME FORM COMPONENT
   ═══════════════════════════════════════════════════════ */
function ResumeForm({ formData, setFormData, api }) {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummaries, setAiSummaries] = useState([]);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillsEdited, setSkillsEdited] = useState(false);

  const update = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  /* ─── AI Summary ─── */
  const generateAiSummary = async () => {
    if (!formData.professionalTitle?.trim()) {
      toast.error('Enter a Professional Title first');
      return;
    }
    setAiLoading(true);
    setAiSummaries([]);
    setShowAiPanel(true);
    try {
      const res = await api.post('/documents/ai-summary', {
        professional_title: formData.professionalTitle,
      });
      setAiSummaries(res.data.summaries || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Suggestion failed. You can type your summary manually.');
    } finally {
      setAiLoading(false);
    }
  };

  const selectSummary = (text) => {
    update('profileSummary', text);
    setShowAiPanel(false);
    toast.success('Summary applied — feel free to edit it');
  };

  /* ─── Work Experience helpers ─── */
  const updateExp = (idx, key, value) => {
    setFormData(prev => {
      const next = [...prev.workExperience];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, workExperience: next };
    });
  };
  const updateResp = (expIdx, respIdx, value) => {
    setFormData(prev => {
      const next = [...prev.workExperience];
      const resps = [...next[expIdx].responsibilities];
      resps[respIdx] = value;
      next[expIdx] = { ...next[expIdx], responsibilities: resps };
      return { ...prev, workExperience: next };
    });
  };
  const addResp = (expIdx) => {
    setFormData(prev => {
      const next = [...prev.workExperience];
      next[expIdx] = { ...next[expIdx], responsibilities: [...next[expIdx].responsibilities, ''] };
      return { ...prev, workExperience: next };
    });
  };
  const removeResp = (expIdx, respIdx) => {
    setFormData(prev => {
      const next = [...prev.workExperience];
      const resps = next[expIdx].responsibilities.filter((_, i) => i !== respIdx);
      next[expIdx] = { ...next[expIdx], responsibilities: resps };
      return { ...prev, workExperience: next };
    });
  };
  const addExperience = () => {
    setFormData(prev => ({
      ...prev,
      workExperience: [...prev.workExperience, { position: '', company: '', jobCity: '', jobState: '', jobCountry: '', startDate: '', endDate: '', responsibilities: ['', '', ''] }],
    }));
  };
  const removeExperience = (idx) => {
    setFormData(prev => ({
      ...prev,
      workExperience: prev.workExperience.filter((_, i) => i !== idx),
    }));
  };

  /* ─── Education helpers ─── */
  const updateEdu = (idx, key, value) => {
    setFormData(prev => {
      const next = [...prev.education];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, education: next };
    });
  };
  const addEducation = () => {
    setFormData(prev => ({
      ...prev,
      education: [...prev.education, { degree: '', fieldOfStudy: '', institution: '', eduCity: '', eduState: '', eduCountry: '', graduationYear: '' }],
    }));
  };
  const removeEducation = (idx) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== idx),
    }));
  };

  /* ─── Skills helpers ─── */
  const updateSkill = (idx, value) => {
    setSkillsEdited(true);
    setFormData(prev => {
      const next = [...prev.skills];
      next[idx] = value;
      return { ...prev, skills: next };
    });
  };
  const addSkill = () => { setSkillsEdited(true); setFormData(prev => ({ ...prev, skills: [...prev.skills, ''] })); };
  const removeSkill = (idx) => { setSkillsEdited(true); setFormData(prev => ({ ...prev, skills: prev.skills.filter((_, i) => i !== idx) })); };

  /* ─── AI Skills Suggestion ─── */
  const suggestSkills = async () => {
    if (!formData.professionalTitle?.trim()) {
      toast.error('Enter a Professional Title first');
      return;
    }
    setSkillsLoading(true);
    try {
      const res = await api.post('/documents/ai-skills', {
        professional_title: formData.professionalTitle,
      });
      const suggested = res.data.skills || [];
      if (suggested.length > 0) {
        setFormData(prev => ({ ...prev, skills: suggested }));
        setSkillsEdited(false);
        toast.success(`${suggested.length} skills suggested — edit as needed`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Skill suggestion failed. Add skills manually.');
    } finally {
      setSkillsLoading(false);
    }
  };

  /* ─── Certification helpers ─── */
  const updateCert = (idx, key, value) => {
    setFormData(prev => {
      const next = [...prev.certifications];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, certifications: next };
    });
  };
  const addCert = () => setFormData(prev => ({
    ...prev,
    certifications: [...prev.certifications, { certificationName: '', issuingBody: '', year: '' }],
  }));
  const removeCert = (idx) => setFormData(prev => ({
    ...prev,
    certifications: prev.certifications.filter((_, i) => i !== idx),
  }));

  /* ─── Reference helpers ─── */
  const updateRef = (idx, key, value) => {
    setFormData(prev => {
      const next = [...prev.references];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, references: next };
    });
  };
  const addRef = () => setFormData(prev => ({
    ...prev,
    references: [...prev.references, {
      referenceName: '', referenceTitle: '', referenceCompany: '',
      referencePhone: '', referenceEmail: '',
    }],
  }));
  const removeRef = (idx) => setFormData(prev => ({
    ...prev,
    references: prev.references.filter((_, i) => i !== idx),
  }));

  return (
    <div className="space-y-6">
      {/* ── Personal Info ── */}
      <Card className="card-elevated" data-testid="personal-info-card">
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2 text-foreground">
            <User className="h-4 w-4 text-primary" /> Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Full Name <span className="text-destructive">*</span></Label>
              <Input value={formData.fullName} onChange={e => update('fullName', e.target.value)} placeholder="John Doe" data-testid="input-full-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Professional Title <span className="text-destructive">*</span></Label>
              <Input value={formData.professionalTitle} onChange={e => update('professionalTitle', e.target.value)} placeholder="Senior Software Engineer" data-testid="input-professional-title" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Email <span className="text-destructive">*</span></Label>
              <Input type="email" value={formData.email} onChange={e => update('email', e.target.value)} placeholder="john@example.com" data-testid="input-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Phone</Label>
              <Input value={formData.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 234 567 8900" data-testid="input-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Years of Experience</Label>
              <Input value={formData.yearsOfExperience} onChange={e => update('yearsOfExperience', e.target.value)} placeholder="5+" data-testid="input-years-experience" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">City</Label>
              <Input value={formData.city} onChange={e => update('city', e.target.value)} placeholder="Lagos" data-testid="input-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">State</Label>
              <Input value={formData.state} onChange={e => update('state', e.target.value)} placeholder="Lagos State" data-testid="input-state" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Country</Label>
              <Input value={formData.country} onChange={e => update('country', e.target.value)} placeholder="Nigeria" data-testid="input-country" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">LinkedIn / Portfolio URL</Label>
            <Input value={formData.linkedinOrPortfolio} onChange={e => update('linkedinOrPortfolio', e.target.value)} placeholder="linkedin.com/in/johndoe" data-testid="input-linkedin" />
          </div>
        </CardContent>
      </Card>

      {/* ── Profile Summary + AI ── */}
      <Card className="card-elevated" data-testid="profile-summary-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-foreground">
              <FileText className="h-4 w-4 text-primary" /> Profile Summary
            </CardTitle>
            <Button
              type="button" variant="outline" size="sm"
              onClick={generateAiSummary}
              disabled={aiLoading}
              data-testid="ai-generate-btn"
              className="text-xs gap-1.5"
            >
              {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              {aiLoading ? 'Generating...' : 'Get Suggestions'}
            </Button>
          </div>
          <CardDescription className="text-xs">Write your summary or get suggestions from your professional title.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={formData.profileSummary}
            onChange={e => update('profileSummary', e.target.value)}
            placeholder="Experienced professional with a proven track record..."
            rows={4}
            data-testid="input-profile-summary"
          />
          {showAiPanel && (
            <div className="space-y-2 p-3 rounded-lg border border-primary/20 bg-primary/5" data-testid="ai-suggestions-panel">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-primary">Suggestions</span>
                <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setShowAiPanel(false)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
              {aiLoading ? (
                <div className="flex items-center gap-2 py-4 justify-center text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating summaries...
                </div>
              ) : aiSummaries.length > 0 ? (
                aiSummaries.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => selectSummary(s)}
                    className="w-full text-left p-3 rounded-md border border-border bg-white hover:border-primary/40 hover:bg-primary/5 transition-colors text-sm leading-relaxed"
                    data-testid={`ai-summary-option-${i}`}
                  >
                    {s}
                  </button>
                ))
              ) : (
                <p className="text-xs text-muted-foreground py-2">No suggestions available. Try a different title or write your own.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Work Experience ── */}
      <Card className="card-elevated" data-testid="work-experience-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-foreground">
              <Briefcase className="h-4 w-4 text-primary" /> Work Experience
            </CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addExperience} data-testid="add-experience-btn" className="text-xs gap-1">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {formData.workExperience.map((exp, idx) => (
            <div key={idx} className="space-y-3 p-4 rounded-lg border border-border bg-muted/30" data-testid={`experience-entry-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">Position #{idx + 1}</span>
                {formData.workExperience.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-destructive" onClick={() => removeExperience(idx)} data-testid={`remove-experience-${idx}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Position / Job Title</Label>
                  <Input value={exp.position} onChange={e => updateExp(idx, 'position', e.target.value)} placeholder="Senior Developer" data-testid={`input-exp-position-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Company</Label>
                  <Input value={exp.company} onChange={e => updateExp(idx, 'company', e.target.value)} placeholder="Tech Corp" data-testid={`input-exp-company-${idx}`} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">City</Label>
                  <Input value={exp.jobCity} onChange={e => updateExp(idx, 'jobCity', e.target.value)} placeholder="Lagos" data-testid={`input-exp-city-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">State</Label>
                  <Input value={exp.jobState} onChange={e => updateExp(idx, 'jobState', e.target.value)} placeholder="Lagos State" data-testid={`input-exp-state-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Country</Label>
                  <Input value={exp.jobCountry} onChange={e => updateExp(idx, 'jobCountry', e.target.value)} placeholder="Nigeria" data-testid={`input-exp-country-${idx}`} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Start Date</Label>
                  <Input value={exp.startDate} onChange={e => updateExp(idx, 'startDate', e.target.value)} placeholder="Jan 2021" data-testid={`input-exp-start-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">End Date</Label>
                  <Input value={exp.endDate} onChange={e => updateExp(idx, 'endDate', e.target.value)} placeholder="Present" data-testid={`input-exp-end-${idx}`} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Key Responsibilities</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-5 text-xs px-1.5" onClick={() => addResp(idx)} data-testid={`add-resp-${idx}`}>
                    <Plus className="h-3 w-3 mr-0.5" /> Add
                  </Button>
                </div>
                {exp.responsibilities.map((r, ri) => (
                  <div key={ri} className="flex items-center gap-2">
                    <Input
                      value={r}
                      onChange={e => updateResp(idx, ri, e.target.value)}
                      placeholder={`Responsibility ${ri + 1}`}
                      className="flex-1"
                      data-testid={`input-exp-resp-${idx}-${ri}`}
                    />
                    {exp.responsibilities.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive" onClick={() => removeResp(idx, ri)}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Education ── */}
      <Card className="card-elevated" data-testid="education-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-foreground">
              <GraduationCap className="h-4 w-4 text-primary" /> Education
            </CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addEducation} data-testid="add-education-btn" className="text-xs gap-1">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.education.map((edu, idx) => (
            <div key={idx} className="space-y-3 p-4 rounded-lg border border-border bg-muted/30" data-testid={`education-entry-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">Qualification #{idx + 1}</span>
                {formData.education.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-destructive" onClick={() => removeEducation(idx)} data-testid={`remove-education-${idx}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Degree</Label>
                  <Input value={edu.degree} onChange={e => updateEdu(idx, 'degree', e.target.value)} placeholder="B.Sc." data-testid={`input-edu-degree-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Field of Study</Label>
                  <Input value={edu.fieldOfStudy} onChange={e => updateEdu(idx, 'fieldOfStudy', e.target.value)} placeholder="Computer Science" data-testid={`input-edu-field-${idx}`} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Institution</Label>
                  <Input value={edu.institution} onChange={e => updateEdu(idx, 'institution', e.target.value)} placeholder="University of Lagos" data-testid={`input-edu-institution-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Graduation Year</Label>
                  <Input value={edu.graduationYear} onChange={e => updateEdu(idx, 'graduationYear', e.target.value)} placeholder="2018" data-testid={`input-edu-year-${idx}`} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">City</Label>
                  <Input value={edu.eduCity} onChange={e => updateEdu(idx, 'eduCity', e.target.value)} placeholder="Lagos" data-testid={`input-edu-city-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">State</Label>
                  <Input value={edu.eduState} onChange={e => updateEdu(idx, 'eduState', e.target.value)} placeholder="Lagos State" data-testid={`input-edu-state-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Country</Label>
                  <Input value={edu.eduCountry} onChange={e => updateEdu(idx, 'eduCountry', e.target.value)} placeholder="Nigeria" data-testid={`input-edu-country-${idx}`} />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Skills ── */}
      <Card className="card-elevated" data-testid="skills-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-foreground">
              <Wrench className="h-4 w-4 text-primary" /> Skills
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                type="button" variant="outline" size="sm"
                onClick={suggestSkills}
                disabled={skillsLoading}
                data-testid="suggest-skills-btn"
                className="text-xs gap-1.5"
              >
                {skillsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {skillsLoading ? 'Suggesting...' : 'Suggest Skills'}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={addSkill} data-testid="add-skill-btn" className="text-xs gap-1">
                <Plus className="h-3 w-3" /> Add
              </Button>
            </div>
          </div>
          <CardDescription className="text-xs">Add skills manually or get suggestions based on your professional title.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {formData.skills.map((skill, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <Input
                  value={skill}
                  onChange={e => updateSkill(idx, e.target.value)}
                  placeholder={`Skill ${idx + 1}`}
                  className="w-36 h-8 text-sm"
                  data-testid={`input-skill-${idx}`}
                />
                {formData.skills.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive" onClick={() => removeSkill(idx)}>
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Certifications ── */}
      <Card className="card-elevated" data-testid="certifications-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-foreground">
              <Award className="h-4 w-4 text-primary" /> Certifications
            </CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addCert} data-testid="add-cert-btn" className="text-xs gap-1">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          <CardDescription className="text-xs">Optional. Add professional certifications.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.certifications.map((cert, idx) => (
            <div key={idx} className="flex items-start gap-3" data-testid={`cert-entry-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-1">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Certificate Name</Label>
                  <Input value={cert.certificationName} onChange={e => updateCert(idx, 'certificationName', e.target.value)} placeholder="AWS Solutions Architect" data-testid={`input-cert-name-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Issuing Body</Label>
                  <Input value={cert.issuingBody} onChange={e => updateCert(idx, 'issuingBody', e.target.value)} placeholder="Amazon Web Services" data-testid={`input-cert-body-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Year</Label>
                  <Input value={cert.year} onChange={e => updateCert(idx, 'year', e.target.value)} placeholder="2023" data-testid={`input-cert-year-${idx}`} />
                </div>
              </div>
              {formData.certifications.length > 1 && (
                <Button type="button" variant="ghost" size="sm" className="mt-6 h-8 w-8 p-0 text-destructive" onClick={() => removeCert(idx)} data-testid={`remove-cert-${idx}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── References (optional) ── */}
      <Card className="card-elevated" data-testid="references-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-foreground">
              <Users className="h-4 w-4 text-primary" /> References
            </CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addRef} data-testid="add-ref-btn" className="text-xs gap-1">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          <CardDescription className="text-xs">
            Optional — leave blank to omit this section from your resume.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.references.map((ref, idx) => (
            <div key={idx} className="flex items-start gap-3" data-testid={`ref-entry-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input value={ref.referenceName} onChange={e => updateRef(idx, 'referenceName', e.target.value)} placeholder="Ada Okafor" data-testid={`input-ref-name-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Position / Title</Label>
                  <Input value={ref.referenceTitle} onChange={e => updateRef(idx, 'referenceTitle', e.target.value)} placeholder="Director of Operations" data-testid={`input-ref-title-${idx}`} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs text-muted-foreground">Company / Organization</Label>
                  <Input value={ref.referenceCompany} onChange={e => updateRef(idx, 'referenceCompany', e.target.value)} placeholder="Tech Solutions Ltd" data-testid={`input-ref-company-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <Input value={ref.referencePhone} onChange={e => updateRef(idx, 'referencePhone', e.target.value)} placeholder="+234 801 234 5678" data-testid={`input-ref-phone-${idx}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input value={ref.referenceEmail} onChange={e => updateRef(idx, 'referenceEmail', e.target.value)} placeholder="ada@example.com" data-testid={`input-ref-email-${idx}`} />
                </div>
              </div>
              {formData.references.length > 1 && (
                <Button type="button" variant="ghost" size="sm" className="mt-6 h-8 w-8 p-0 text-destructive" onClick={() => removeRef(idx)} data-testid={`remove-ref-${idx}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   GENERIC FORM (for non-resume document types)
   ═══════════════════════════════════════════════════════ */
function GenericDocumentForm({ formData, setFormData, documentType, showCurrency, showLogo, logoFile, setLogoFile, logoPreview, setLogoPreview, api }) {

  const [aiSupplyNoteLoading, setAiSupplyNoteLoading] = useState(false);
  // Only currency-driven tax-label updates until the user picks a label themselves.
  const taxLabelTouchedRef = useRef(false);

  // Rerun / sample loads: if saved label already differs from currency default, don't
  // silently override it when the user later changes currency.
  useEffect(() => {
    if (!showCurrency) return;
    const current = formData.tax_label_type;
    if (!current) return;
    if (current !== defaultTaxLabelForCurrency(formData.currency)) {
      taxLabelTouchedRef.current = true;
    }
  }, [showCurrency, formData.tax_label_type, formData.currency]);

  const updateField = (path, value) => {
    setFormData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        obj = obj[k];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleCurrencyChange = (v) => {
    setFormData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.currency = v;
      if (!taxLabelTouchedRef.current) {
        next.tax_label_type = defaultTaxLabelForCurrency(v);
      }
      return next;
    });
  };

  const handleTaxLabelTypeChange = (v) => {
    taxLabelTouchedRef.current = true;
    setFormData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.tax_label_type = v;
      if (v !== 'Custom') next.tax_custom_name = next.tax_custom_name || '';
      return next;
    });
  };

  const addArrayItem = (arrayPath, template) => {
    setFormData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = arrayPath.split('.');
      let obj = next;
      for (const k of keys) obj = obj[isNaN(k) ? k : parseInt(k)];
      obj.push(JSON.parse(JSON.stringify(template)));
      return next;
    });
  };

  const removeArrayItem = (arrayPath, index) => {
    setFormData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = arrayPath.split('.');
      let obj = next;
      for (const k of keys) obj = obj[isNaN(k) ? k : parseInt(k)];
      obj.splice(index, 1);
      return next;
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const removeLogo = () => {
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
  };

  const generateSupplyNote = async () => {
    const poTitle = (formData.po_title || '').trim();
    if (!poTitle) {
      toast.error('Enter a PO Title first (e.g. "Supply of Industrial Batteries"), then try again.');
      return;
    }
    setAiSupplyNoteLoading(true);
    try {
      const res = await api.post('/documents/ai-supply-note', { po_title: poTitle });
      const note = res.data?.supply_note || '';
      if (!note) throw new Error('Empty AI response');
      updateField('supply_note', note);
      toast.success('Scope of supply drafted — feel free to edit.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not generate supply note. Please try again.';
      toast.error(msg);
    } finally {
      setAiSupplyNoteLoading(false);
    }
  };

  const renderField = (key, value, path) => {
    if (value === null || value === undefined) return null;
    if (key === 'currency') return null;
    // Tax fields / Quotation discount are placed after the items list (totals block).
    if (showCurrency && ['tax_rate', 'tax_label_type', 'tax_custom_name', 'discount_amount'].includes(key)) {
      return null;
    }

    if (documentType === 'purchase_order' && path.endsWith('.uom')) {
      return (
        <div key={path} className="space-y-1" data-testid={`field-${path}`}>
          <Label className="text-xs text-muted-foreground capitalize">Unit of measure</Label>
          <Input
            value={value}
            onChange={(e) => updateField(path, e.target.value)}
            placeholder="e.g. EA, PCS, LOT, KG, MT, L, SET, BOX"
            data-testid={`input-${path}`}
          />
        </div>
      );
    }

    if (documentType === 'purchase_order' && path === 'delivery_date') {
      return (
        <div key={path} className="space-y-1" data-testid={`field-${path}`}>
          <Label className="text-xs text-muted-foreground">Delivery Date</Label>
          <Input
            type="date"
            value={value}
            onChange={(e) => updateField(path, e.target.value)}
            data-testid={`input-${path}`}
          />
        </div>
      );
    }

    if (documentType === 'purchase_order' && path === 'payment_terms') {
      const preset = PO_PAYMENT_TERM_PRESETS.includes(value) ? value : 'Custom';
      const showCustom = preset === 'Custom';
      return (
        <div key={path} className="space-y-2" data-testid={`field-${path}`}>
          <Label className="text-xs text-muted-foreground">Payment Terms</Label>
          <select
            value={preset}
            onChange={(e) => {
              const next = e.target.value;
              if (next === 'Custom') {
                if (PO_PAYMENT_TERM_PRESETS.includes(value)) updateField(path, '');
              } else {
                updateField(path, next);
              }
            }}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            data-testid={`select-${path}-preset`}
          >
            {PO_PAYMENT_TERM_PRESETS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {showCustom && (
            <Input
              value={PO_PAYMENT_TERM_PRESETS.includes(value) ? '' : value}
              onChange={(e) => updateField(path, e.target.value)}
              placeholder="Enter custom payment terms"
              data-testid={`input-${path}`}
            />
          )}
        </div>
      );
    }

    if (documentType === 'purchase_order' && path === 'terms') {
      return (
        <div key={path} className="space-y-2" data-testid={`field-${path}`}>
          <Label className="text-sm font-semibold">Terms &amp; Conditions</Label>
          <Textarea
            value={value}
            onChange={(e) => updateField(path, e.target.value)}
            rows={6}
            placeholder="Standard delivery, payment, warranty, and governing-law terms…"
            data-testid={`input-${path}`}
            className="resize-y"
          />
        </div>
      );
    }

    // ── Purchase Order: bespoke renderer for SUPPLY_NOTE (Textarea + AI button) ──
    if (documentType === 'purchase_order' && path === 'supply_note') {
      return (
        <div key={path} className="space-y-2" data-testid={`field-${path}`}>
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Scope of Supply</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={generateSupplyNote}
              disabled={aiSupplyNoteLoading}
              data-testid="ai-generate-supply-note-btn"
              className="h-7 px-2.5 text-xs"
            >
              {aiSupplyNoteLoading
                ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Drafting…</>
                : <><Sparkles className="h-3 w-3 mr-1" /> Get Suggestions</>}
            </Button>
          </div>
          <Textarea
            value={value}
            onChange={(e) => updateField(path, e.target.value)}
            rows={5}
            placeholder="Describe what's being supplied, delivery & quality expectations, installation/testing requirements…"
            data-testid={`input-${path}`}
            className="resize-y"
          />
          <p className="text-xs text-muted-foreground">
            Tip: enter the PO Title first, then click <span className="font-medium">Get Suggestions</span> for a polished procurement-grade draft you can edit.
          </p>
        </div>
      );
    }

    if (Array.isArray(value)) {
      const itemTemplate = value.length > 0
        ? (typeof value[0] === 'object' ? Object.fromEntries(Object.keys(value[0]).map(k => [k, typeof value[0][k] === 'number' ? 0 : ''])) : '')
        : '';
      return (
        <div key={path} className="space-y-3" data-testid={`field-${path}`}>
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold capitalize">{key.replace(/_/g, ' ')}</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem(path, itemTemplate)} data-testid={`add-${path}`}>
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          {value.map((item, idx) => (
            <div key={idx} className="pl-4 border-l-2 border-primary/20 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground font-medium">#{idx + 1}</span>
                {value.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-destructive" onClick={() => removeArrayItem(path, idx)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {typeof item === 'object'
                ? Object.entries(item).map(([k, v]) => renderField(k, v, `${path}.${idx}.${k}`))
                : <Input value={item} onChange={(e) => updateField(`${path}.${idx}`, e.target.value)} data-testid={`input-${path}-${idx}`} />
              }
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'object') {
      return (
        <div key={path} className="space-y-3 pl-4 border-l-2 border-muted" data-testid={`field-${path}`}>
          <Label className="text-sm font-semibold capitalize">{key.replace(/_/g, ' ')}</Label>
          {Object.entries(value).map(([k, v]) => renderField(k, v, `${path}.${k}`))}
        </div>
      );
    }

    if (typeof value === 'number') {
      return (
        <div key={path} className="space-y-1" data-testid={`field-${path}`}>
          <Label className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</Label>
          <Input type="number" step="any" value={value} onChange={(e) => updateField(path, parseFloat(e.target.value) || 0)} data-testid={`input-${path}`} />
        </div>
      );
    }

    return (
      <div key={path} className="space-y-1" data-testid={`field-${path}`}>
        <Label className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</Label>
        <Input value={value} onChange={(e) => updateField(path, e.target.value)} data-testid={`input-${path}`} />
      </div>
    );
  };

  const taxTotalsSection = showCurrency ? (
    <div className="space-y-3 pt-2 border-t border-border/40" data-testid="tax-options-section">
      <p className="text-sm font-semibold text-foreground">Totals</p>
      {formData.discount_amount !== undefined && formData.discount_amount !== null && (
        <div className="space-y-1" data-testid="field-discount_amount">
          <Label className="text-xs text-muted-foreground">Discount Amount</Label>
          <Input
            type="number"
            step="any"
            value={formData.discount_amount ?? 0}
            onChange={(e) => updateField('discount_amount', parseFloat(e.target.value) || 0)}
            data-testid="input-discount_amount"
          />
        </div>
      )}
      <div className="space-y-1" data-testid="tax-label-field">
        <Label className="text-xs text-muted-foreground">Tax Label</Label>
        <select
          value={formData.tax_label_type || defaultTaxLabelForCurrency(formData.currency)}
          onChange={(e) => handleTaxLabelTypeChange(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          data-testid="select-tax-label-type"
        >
          {TAX_LABEL_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
      {formData.tax_label_type === 'Custom' && (
        <div className="space-y-1" data-testid="tax-custom-name-field">
          <Label className="text-xs text-muted-foreground">Custom Tax Name</Label>
          <Input
            value={formData.tax_custom_name || ''}
            onChange={(e) => updateField('tax_custom_name', e.target.value)}
            placeholder="e.g. Service Tax, Excise Duty"
            data-testid="input-tax-custom-name"
          />
        </div>
      )}
      <div className="space-y-1" data-testid="tax-rate-field">
        <Label className="text-xs text-muted-foreground">Tax Rate (%)</Label>
        <Input
          type="number"
          step="any"
          value={formData.tax_rate ?? 0}
          onChange={(e) => updateField('tax_rate', parseFloat(e.target.value) || 0)}
          data-testid="input-tax-rate"
        />
      </div>
    </div>
  ) : null;

  return (
    <>
      {(showCurrency || showLogo) && (
        <Card className="card-elevated" data-testid="options-card">
          <CardHeader><CardTitle className="text-foreground text-base">Document Options</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {showCurrency && (
              <div className="space-y-1" data-testid="currency-field">
                <Label className="text-xs text-muted-foreground">Currency</Label>
                <CurrencySelector
                  value={formData.currency || 'USD'}
                  onChange={handleCurrencyChange}
                />
              </div>
            )}
            {showLogo && (
              <div className="space-y-2" data-testid="logo-field">
                <Label className="text-xs text-muted-foreground">Company Logo (optional)</Label>
                {logoPreview ? (
                  <div className="flex items-center gap-4">
                    <div className="relative w-24 h-24 rounded-lg border overflow-hidden bg-white flex items-center justify-center">
                      <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">{logoFile?.name}</p>
                      <div className="flex gap-2">
                        <label className="cursor-pointer">
                          <Button type="button" variant="outline" size="sm" asChild><span><Upload className="h-3 w-3 mr-1" /> Replace</span></Button>
                          <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                        </label>
                        <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={removeLogo} data-testid="remove-logo-btn"><X className="h-3 w-3 mr-1" /> Remove</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="cursor-pointer flex items-center justify-center w-full h-24 border-2 border-dashed rounded-lg hover:bg-muted/40 transition-colors" data-testid="logo-upload-area">
                    <div className="text-center text-muted-foreground">
                      <Image className="h-6 w-6 mx-auto mb-1" />
                      <p className="text-xs">Click to upload logo</p>
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                  </label>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="card-elevated" data-testid="form-card">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Document Details</CardTitle>
          <CardDescription>Fill in the fields below. Sample data is pre-filled.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(formData).map(([key, value]) => {
            const field = renderField(key, value, key);
            if (key === 'items' && taxTotalsSection) {
              return (
                <div key="items-and-totals" className="space-y-4">
                  {field}
                  {taxTotalsSection}
                </div>
              );
            }
            return field;
          })}
        </CardContent>
      </Card>
    </>
  );
}


/* ═══════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════ */
export default function DocumentGenerationPage() {
  const { documentType: slug } = useParams();
  const [searchParams] = useSearchParams();
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const documentType = slugToBackendType(slug);
  const urlTemplateId = searchParams.get('templateId');
  const urlTemplateName = searchParams.get('templateName');
  const rerunJobId = searchParams.get('rerunJobId');

  const isResume = documentType === 'resume';
  const isCoverLetterPage = documentType === 'cover_letter';
  const resultCardRef = useRef(null);
  const resumeSectionRef = useRef(null);
  /** Restore window scroll after job completion when top banner is not shown. */
  const scrollRestoreYRef = useRef(null);

  const [docMeta, setDocMeta] = useState(null);
  const [formData, setFormData] = useState(isResume ? { ...DEFAULT_RESUME } : {});
  const [generating, setGenerating] = useState(false);
  const [jobResult, setJobResult] = useState(null);
  const [polling, setPolling] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  // Resolved template — can come from URL or rerun-data
  const [resolvedTemplateId, setResolvedTemplateId] = useState(urlTemplateId || null);
  const [resolvedTemplateName, setResolvedTemplateName] = useState(urlTemplateName || null);
  const [availableTemplates, setAvailableTemplates] = useState([]);

  // Cover letter package page (standalone / reuse) — resume + cover letter sections
  const [resumePackageData, setResumePackageData] = useState({ ...DEFAULT_RESUME });
  const [resumeJobResult, setResumeJobResult] = useState(null);
  const [resumeTemplateId, setResumeTemplateId] = useState(null);
  const [resumeCredits, setResumeCredits] = useState(3);
  const [resumeGenerating, setResumeGenerating] = useState(false);
  const [resumeBaseline, setResumeBaseline] = useState(null);
  const [coverLetterBaseline, setCoverLetterBaseline] = useState(null);
  const [pairedResumeJobId, setPairedResumeJobId] = useState(null);

  // Resume & Cover Letter post-generation flow (resume only)
  const [resumePostStep, setResumePostStep] = useState(null);
  const [coverLetterSkipped, setCoverLetterSkipped] = useState(false);
  const [coverContext, setCoverContext] = useState({ ...DEFAULT_COVER_CONTEXT });
  const [coverDraft, setCoverDraft] = useState('');
  const [coverDraftLoading, setCoverDraftLoading] = useState(false);
  const [coverLetterJobResult, setCoverLetterJobResult] = useState(null);
  const [coverLetterTemplateId, setCoverLetterTemplateId] = useState(null);
  const [coverLetterGenerating, setCoverLetterGenerating] = useState(false);
  const [coverLetterCredits, setCoverLetterCredits] = useState(2);

  // Aliases for downstream usage
  const templateId = resolvedTemplateId;
  const templateName = resolvedTemplateName;

  useEffect(() => {
    fetchDocType();
  }, [documentType]);

  useLayoutEffect(() => {
    if (scrollRestoreYRef.current == null) return;
    const y = scrollRestoreYRef.current;
    scrollRestoreYRef.current = null;
    window.scrollTo({ top: y, left: 0, behavior: 'instant' });
  }, [jobResult]);

  useEffect(() => {
    if (!isResume || !jobResult) return;
    if (jobResult.status !== 'preview_ready' && jobResult.status !== 'completed') return;
    if (resumePostStep === null) {
      setResumePostStep('cover_prompt');
    }
  }, [isResume, jobResult, resumePostStep]);

  useEffect(() => {
    if (!isResume || resumePostStep !== 'cover_draft') return;
    api.get('/templates/by-type/cover_letter')
      .then((res) => {
        const templates = res.data?.templates || res.data;
        if (templates?.length) {
          setCoverLetterTemplateId(templates[0].id);
        }
      })
      .catch(() => { /* template may be seeded on next deploy */ });
  }, [isResume, resumePostStep, api]);

  useEffect(() => {
    if (!isCoverLetterPage) return;
    if (!urlTemplateId && !resolvedTemplateId) {
      setResolvedTemplateId(COVER_LETTER_TEMPLATE_ID);
      setResolvedTemplateName(COVER_LETTER_TEMPLATE_NAME);
      setCoverLetterTemplateId(COVER_LETTER_TEMPLATE_ID);
    }
  }, [isCoverLetterPage, urlTemplateId, resolvedTemplateId]);

  useEffect(() => {
    if (!isCoverLetterPage) return;
    api.get('/templates/by-type/cover_letter')
      .then((res) => {
        const templates = res.data?.templates || res.data;
        if (templates?.length) {
          setCoverLetterTemplateId((prev) => prev || templates[0].id);
        }
      })
      .catch(() => { /* template may be seeded on next deploy */ });
    api.get('/templates/by-type/resume')
      .then((res) => {
        const templates = res.data?.templates || res.data;
        if (templates?.length) {
          setResumeTemplateId((prev) => prev || templates[0].id);
        }
      })
      .catch(() => { /* ignore */ });
  }, [isCoverLetterPage, api]);

  const fetchDocType = async () => {
    try {
      const res = await api.get('/documents/types');
      const found = res.data.types.find(t => t.id === documentType);
      if (!found) { toast.error('Invalid document type'); navigate('/dashboard'); return; }
      setDocMeta(found);

      const coverType = res.data.types.find((t) => t.id === 'cover_letter');
      if (coverType?.credits) {
        setCoverLetterCredits(coverType.credits);
      }
      const resumeType = res.data.types.find((t) => t.id === 'resume');
      if (resumeType?.credits) {
        setResumeCredits(resumeType.credits);
      }

      // If re-running, load original job's input data AND template
      if (rerunJobId) {
        try {
          const [rerunRes, logosRes] = await Promise.all([
            api.get(`/jobs/${rerunJobId}/rerun-data`),
            LOGO_DOC_TYPES.includes(documentType)
              ? api.get(`/documents/${rerunJobId}/logos`).catch(() => ({ data: { logo: null } }))
              : Promise.resolve({ data: { logo: null } }),
          ]);
          const rd = rerunRes.data;
          const inputData = rd.input_data;

          // Store available templates for switcher
          if (rd.available_templates?.length) {
            setAvailableTemplates(rd.available_templates);
          }

          // Auto-resolve template from original job when not in URL
          if (!urlTemplateId && rd.template_id) {
            setResolvedTemplateId(rd.template_id);
            const tpl = (rd.available_templates || []).find(t => t.id === rd.template_id);
            if (tpl) setResolvedTemplateName(tpl.name);
          }

          if (inputData && Object.keys(inputData).length > 0) {
            if (isResume) {
              setFormData(mapTemplateKeysToResume(inputData));
            } else if (isCoverLetterPage) {
              const { resume, cover, pairedResumeJobId: pairedId } = splitCoverLetterInputData(inputData);
              setResumePackageData(resume);
              setResumeBaseline(JSON.stringify(resume));
              setCoverContext({
                jobTitle: cover.jobTitle,
                companyName: cover.companyName,
                jobDescription: cover.jobDescription,
                tone: cover.tone,
              });
              setCoverDraft(cover.coverLetterBody);
              setCoverLetterBaseline(JSON.stringify({
                context: {
                  jobTitle: cover.jobTitle,
                  companyName: cover.companyName,
                  jobDescription: cover.jobDescription,
                  tone: cover.tone,
                },
                draft: cover.coverLetterBody,
              }));
              const linkedResumeId = pairedId || rd.metadata?.paired_resume_job_id || null;
              setPairedResumeJobId(linkedResumeId);

              try {
                const coverJobRes = await api.get(`/jobs/${rerunJobId}`);
                if (isJobDownloadReady(coverJobRes.data)) {
                  setCoverLetterJobResult(coverJobRes.data);
                }
              } catch { /* ignore */ }

              if (linkedResumeId) {
                try {
                  const resumeJobRes = await api.get(`/jobs/${linkedResumeId}`);
                  if (isJobDownloadReady(resumeJobRes.data)) {
                    setResumeJobResult(resumeJobRes.data);
                  }
                } catch { /* ignore */ }
              }
            } else {
              setFormData(JSON.parse(JSON.stringify(inputData)));
            }

            // Restore uploaded logo for commercial docs (invoice/quotation/etc.).
            // Mirrors Business Plan / proposal wizards — logo bytes live in job_logos.
            if (LOGO_DOC_TYPES.includes(documentType) && logosRes?.data?.logo) {
              const restored = dataURIToFile(logosRes.data.logo, 'restored-logo');
              if (restored) {
                setLogoFile(restored);
                setLogoPreview((prev) => {
                  if (prev) URL.revokeObjectURL(prev);
                  return URL.createObjectURL(restored);
                });
                toast.info('Previous form data and logo loaded. Edit and submit when ready.');
                return;
              }
            }

            toast.info('Previous form data loaded. Edit and submit when ready.');
            return;
          }
        } catch { /* fall through to defaults */ }
      }

      // For fresh generation, load available templates from templates API
      if (!rerunJobId) {
        try {
          const tplRes = await api.get(`/templates/by-type/${documentType}`);
          // API returns { templates: [...], document_type: "..." }
          const templates = tplRes.data?.templates || tplRes.data;
          if (templates?.length) {
            setAvailableTemplates(templates.map(t => ({ id: t.id, name: t.name, category: t.category || '' })));
          }
        } catch { /* no templates — will use HTML fallback */ }
      }

      // Set defaults
      if (isResume) {
        setFormData({ ...DEFAULT_RESUME });
      } else if (isCoverLetterPage) {
        setResumePackageData({ ...DEFAULT_RESUME });
        setCoverContext({ ...DEFAULT_COVER_CONTEXT });
        setCoverDraft('');
      } else {
        setFormData(JSON.parse(JSON.stringify(found.sample_data)));
      }
    } catch { toast.error('Failed to load'); }
  };

  /* ─── Validation ─── */
  const validateResumeFields = (data) => {
    const errors = {};
    if (!data.fullName?.trim()) errors.fullName = 'Full name is required';
    if (!data.professionalTitle?.trim()) errors.professionalTitle = 'Professional title is required';
    if (!data.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
      errors.email = 'Enter a valid email address';
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the highlighted fields');
    }
    return Object.keys(errors).length === 0;
  };

  const validateResume = () => validateResumeFields(formData);

  /* ─── Generate (balance checked at enqueue; credits charged on success) ─── */
  const handleGenerate = async () => {
    if (isResume && !validateResume()) return;

    setGenerating(true);
    if (isResume) {
      setJobResult(null);
      setResumePostStep(null);
      setCoverLetterSkipped(false);
      setCoverContext({ ...DEFAULT_COVER_CONTEXT });
      setCoverDraft('');
      setCoverLetterJobResult(null);
    }
    try {
      const payload = isResume ? mapResumeToTemplateKeys(formData) : formData;

      let res;
      if (logoFile && templateId) {
        const fd = new FormData();
        fd.append('service_type', 'document_generation');
        fd.append('document_type', documentType);
        fd.append('template_id', templateId);
        fd.append('input_data', JSON.stringify(payload));
        fd.append('logo', logoFile);
        res = await api.post('/documents/create-with-logo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await api.post('/documents/create', {
          service_type: 'document_generation',
          document_type: documentType,
          template_id: templateId || null,
          input_data: payload,
        });
      }
      toast.success('Generation started!');
      pollJob(res.data.job_id, { scrollOnComplete: isResume });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Generation failed';
      if (error.response?.status === 402) {
        toast.error('Insufficient credits. Please add credits before generating.');
      } else {
        toast.error(detail);
      }
      setGenerating(false);
    }
  };

  const pollJob = async (jobId, { scrollOnComplete = true } = {}) => {
    setPolling(true);
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await api.get(`/jobs/${jobId}`);
        // Phase 2: 'preview_ready' is the "done" terminal state for free preview.
        // The legacy 'completed' is also accepted (e.g. older jobs prior to upgrade).
        if (res.data.status === 'preview_ready' || res.data.status === 'completed') {
          if (!scrollOnComplete) {
            scrollRestoreYRef.current = window.scrollY;
          }
          setJobResult(res.data);
          setPolling(false);
          setGenerating(false);
          refreshUser?.();
          toast.success('Your document is ready.');
          if (scrollOnComplete) scrollToResultCard();
          return;
        }
        if (res.data.status === 'failed') {
          setJobResult(res.data);
          setPolling(false);
          setGenerating(false);
          toast.error(res.data.error_message || 'Generation failed');
          return;
        }
      } catch { /* keep polling */ }
    }
    setPolling(false);
    setGenerating(false);
    toast.error('Generation timed out');
  };

  const pollResumeJob = async (jobId) => {
    setResumeGenerating(true);
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const res = await api.get(`/jobs/${jobId}`);
        if (res.data.status === 'preview_ready' || res.data.status === 'completed') {
          setResumeJobResult(res.data);
          setResumeBaseline(JSON.stringify(resumePackageData));
          setPairedResumeJobId(res.data.id);
          setResumeGenerating(false);
          refreshUser?.();
          toast.success('Your resume is ready.');
          scrollToResultCard();
          return;
        }
        if (res.data.status === 'failed') {
          setResumeJobResult(res.data);
          setResumeGenerating(false);
          toast.error(res.data.error_message || 'Resume generation failed');
          return;
        }
      } catch { /* keep polling */ }
    }
    setResumeGenerating(false);
    toast.error('Resume generation timed out');
  };

  const handleGenerateResumePackage = async () => {
    if (!validateResumeFields(resumePackageData)) return;

    setResumeGenerating(true);
    setResumeJobResult(null);
    try {
      const res = await api.post('/documents/create', {
        service_type: 'document_generation',
        document_type: 'resume',
        template_id: resumeTemplateId || null,
        input_data: mapResumeToTemplateKeys(resumePackageData),
      });
      toast.success('Resume generation started!');
      pollResumeJob(res.data.job_id);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Resume generation failed';
      if (error.response?.status === 402) {
        toast.error('Insufficient credits. Please add credits before generating.');
      } else {
        toast.error(detail);
      }
      setResumeGenerating(false);
    }
  };

  const handleSkipCoverLetter = () => {
    setCoverLetterSkipped(true);
    setResumePostStep('download');
  };

  const validateCoverContext = () => {
    const errors = {};
    if (!coverContext.jobTitle?.trim()) errors.jobTitle = 'Job title is required';
    if (!coverContext.companyName?.trim()) errors.companyName = 'Company name is required';
    if (!coverContext.jobDescription?.trim()) errors.jobDescription = 'Job description is required';
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please complete the job details to continue');
    }
    return Object.keys(errors).length === 0;
  };

  const handleContinueToCoverDraft = () => {
    if (!validateCoverContext()) return;
    setResumePostStep('cover_draft');
  };

  const fetchCoverLetterDraft = async () => {
    const resumeSource = isCoverLetterPage ? resumePackageData : formData;
    if (!validateCoverContext()) return;
    setCoverDraftLoading(true);
    try {
      const res = await api.post('/documents/ai-cover-letter-draft', {
        resume_data: mapResumeToTemplateKeys(resumeSource),
        job_title: coverContext.jobTitle.trim(),
        company_name: coverContext.companyName.trim(),
        job_description: coverContext.jobDescription.trim(),
        tone: coverContext.tone,
      });
      setCoverDraft(res.data?.draft || '');
      toast.success('Cover letter draft ready — edit as needed.');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Could not generate cover letter draft';
      toast.error(detail);
    } finally {
      setCoverDraftLoading(false);
    }
  };

  const pollCoverLetterJob = async (jobId) => {
    setCoverLetterGenerating(true);
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const res = await api.get(`/jobs/${jobId}`);
        if (res.data.status === 'preview_ready' || res.data.status === 'completed') {
          setCoverLetterJobResult(res.data);
          setCoverLetterGenerating(false);
          setCoverLetterBaseline(JSON.stringify({
            context: coverContext,
            draft: coverDraft,
          }));
          if (isResume) {
            setResumePostStep('download');
          }
          refreshUser?.();
          toast.success('Your cover letter is ready.');
          scrollToResultCard();
          return;
        }
        if (res.data.status === 'failed') {
          setCoverLetterJobResult(res.data);
          setCoverLetterGenerating(false);
          toast.error(res.data.error_message || 'Cover letter generation failed');
          return;
        }
      } catch { /* keep polling */ }
    }
    setCoverLetterGenerating(false);
    toast.error('Cover letter generation timed out');
  };

  const handleGenerateCoverLetter = async () => {
    if (!coverDraft.trim()) {
      toast.error('Generate or write your cover letter draft first');
      return;
    }
    setCoverLetterGenerating(true);
    setCoverLetterJobResult(null);
    try {
      const resumeSource = isCoverLetterPage ? resumePackageData : formData;
      const pairedId = isCoverLetterPage
        ? (resumeJobResult?.id || pairedResumeJobId)
        : jobResult?.id;
      const payload = mapCoverLetterPayload(resumeSource, coverContext, coverDraft, pairedId);
      const res = await api.post('/documents/create', {
        service_type: 'document_generation',
        document_type: 'cover_letter',
        template_id: coverLetterTemplateId || templateId || null,
        input_data: payload,
      });
      toast.success('Cover letter generation started!');
      pollCoverLetterJob(res.data.job_id);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Cover letter generation failed';
      if (error.response?.status === 402) {
        toast.error('Insufficient credits. Please add credits before generating.');
      } else {
        toast.error(detail);
      }
      setCoverLetterGenerating(false);
    }
  };

  const downloadJobFile = async (job, fallbackName) => {
    if (!job) return false;

    let currentJob = job;
    if (currentJob.status === 'preview_ready') {
      try {
        const fin = await api.post(`/documents/${currentJob.id}/finalize-download`);
        if (fin.data?.balance != null) {
          currentJob = { ...currentJob, status: 'completed' };
          if (job.id === jobResult?.id) {
            setJobResult(currentJob);
          } else if (job.id === coverLetterJobResult?.id) {
            setCoverLetterJobResult(currentJob);
          } else if (job.id === resumeJobResult?.id) {
            setResumeJobResult(currentJob);
          }
        }
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || 'Could not unlock document. No credits charged.';
        if (status === 402) {
          toast.error('Not enough credits. Please add credits and try again.');
          navigate('/credits');
        } else {
          toast.error(detail);
        }
        return false;
      }
    }

    try {
      const res = await api.get(`/storage/download/${currentJob.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = currentJob.metadata?.filename || `${fallbackName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Download failed. The file may no longer be available.';
      toast.error(detail);
      return false;
    }
  };

  const handleDownload = async () => {
    await downloadJobFile(jobResult, documentType);
  };

  const handleDownloadResumePackage = async () => {
    await downloadJobFile(resumeJobResult, 'resume');
  };

  const handleDownloadCoverLetter = async () => {
    await downloadJobFile(coverLetterJobResult, 'cover_letter');
  };

  const handleDownloadBoth = async () => {
    const resumeJob = isCoverLetterPage ? resumeJobResult : jobResult;
    const resumeOk = await downloadJobFile(resumeJob, 'resume');
    if (!resumeOk) return;
    if (coverLetterJobResult) {
      await new Promise((r) => setTimeout(r, 400));
      await downloadJobFile(coverLetterJobResult, 'cover_letter');
    }
  };

  const handleScrollToResumeSection = () => {
    resumeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const resumeFlowStepNum = (() => {
    if (!isResume || !jobResult) return 1;
    if (resumePostStep === 'cover_prompt') return 2;
    if (resumePostStep === 'cover_draft') return 3;
    if (resumePostStep === 'download') return 4;
    return 2;
  })();

  const showResumePostFlow = isResume && jobResult
    && (jobResult.status === 'preview_ready' || jobResult.status === 'completed')
    && resumePostStep;

  const showImmediateDownload = !isCoverLetterPage && jobResult
    && (jobResult.status === 'preview_ready' || jobResult.status === 'completed')
    && (!isResume || resumePostStep === 'download');

  const packageResumeReady = isCoverLetterPage && isJobDownloadReady(resumeJobResult);
  const packageCoverReady = isCoverLetterPage && isJobDownloadReady(coverLetterJobResult);
  const showCoverLetterPackageDownload = isCoverLetterPage && (packageResumeReady || packageCoverReady);

  const resumeDirty = isCoverLetterPage && resumeBaseline !== null
    && JSON.stringify(resumePackageData) !== resumeBaseline;
  const hasCompletedResumePackage = packageResumeReady && !resumeDirty;
  const showResumeGenerateBtn = isCoverLetterPage && (!hasCompletedResumePackage || resumeDirty);

  const coverLetterSnapshot = JSON.stringify({ context: coverContext, draft: coverDraft });
  const coverLetterDirty = isCoverLetterPage && coverLetterBaseline !== null
    && coverLetterSnapshot !== coverLetterBaseline;
  const hasCoverLetterFields = Boolean(
    coverContext.companyName?.trim()
    && coverContext.jobTitle?.trim()
    && coverDraft.trim(),
  );
  const showCoverGenerateBtn = isCoverLetterPage && hasCoverLetterFields
    && (!packageCoverReady || coverLetterDirty);

  const primaryDownloadDocType = isCoverLetterPage ? 'resume' : documentType;
  const primaryDownloadJob = isCoverLetterPage ? resumeJobResult : jobResult;
  const primaryDownloadCredits = isCoverLetterPage ? resumeCredits : (docMeta?.credits ?? 0);
  const primaryDownloadLabel = getDocumentDownloadLabel(
    primaryDownloadDocType,
    primaryDownloadJob?.status,
    primaryDownloadCredits,
  );

  /** Resume/cover-letter package keep top download actions; generic doc types use bottom bar only. */
  const showTopDownloadActions = showCoverLetterPackageDownload
    || (showImmediateDownload && isResume);

  /** Top status banner only where download actions live at the top (not generic invoice-family flows). */
  const showTopResultBanner = showCoverLetterPackageDownload
    || (showImmediateDownload && isResume);

  const showResumeForm = !isCoverLetterPage && (!isResume || !jobResult || jobResult.status === 'failed');
  const showCoverLetterPackageForm = isCoverLetterPage;

  if (!docMeta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const showLogo = LOGO_DOC_TYPES.includes(documentType);
  const showCurrency = ['invoice', 'quotation', 'proforma_invoice', 'purchase_order'].includes(documentType);

  return (
    <div className="min-h-screen bg-muted/20">
      <header className="border-b border-border bg-white safe-area-top">
        <div className="max-w-4xl mx-auto flex items-center justify-between h-14 sm:h-16 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-10 w-10 min-w-[40px]" onClick={() => navigate(isCoverLetterPage ? '/dashboard' : `/documents/${slug}/templates`)} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <DocManiaLogoImage size={28} />
            <span className="text-lg font-bold text-foreground">{docMeta.name}</span>
            <Badge className="bg-primary/10 text-primary border-0">{docMeta.credits} credits</Badge>
            {templateName && (
              <Badge variant="outline" className="ml-2 text-xs">Template: {decodeURIComponent(templateName)}</Badge>
            )}
          </div>
          <CreditBadge balance={user?.credit_balance} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 lg:p-10 space-y-6" style={{ overflowAnchor: 'none' }}>
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">
            Generate {docMeta.name}
          </h1>
          <p className="text-muted-foreground text-sm">{docMeta.description}</p>
        </div>

        {/* Resume ATS marketing banner */}
        {isResume && !jobResult && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-primary/8 to-primary/3 border border-primary/15" data-testid="ats-banner">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="h-4.5 w-4.5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm">
                  Build ATS-Optimized Resumes in Seconds — Smart summaries and skills tailored to your professional title.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Automatically generate recruiter-ready summaries and role-specific skills to boost your chances of getting shortlisted.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Template Selector — visible when multiple templates are available */}
        {availableTemplates.length > 1 && !jobResult && (
          <Card className="card-elevated" data-testid="template-selector-card">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5 min-w-0">
                  <label className="text-sm font-medium text-foreground">Template</label>
                  <p className="text-xs text-muted-foreground truncate">
                    {templateId === urlTemplateId ? 'Selected from template gallery' : rerunJobId ? 'From original job — switch if needed' : 'Choose a template'}
                  </p>
                </div>
                <select
                  className="h-9 min-w-[200px] max-w-[320px] rounded-md border border-input bg-background px-3 text-sm"
                  value={templateId || ''}
                  onChange={e => {
                    const tid = e.target.value;
                    setResolvedTemplateId(tid);
                    const tpl = availableTemplates.find(t => t.id === tid);
                    setResolvedTemplateName(tpl?.name || '');
                  }}
                  data-testid="template-switcher"
                >
                  {availableTemplates.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.id === urlTemplateId ? ' (selected)' : ''}{rerunJobId && t.id === urlTemplateId ? '' : rerunJobId && t.id === resolvedTemplateId && !urlTemplateId ? ' (original)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resume post-generation wizard rail */}
        {showResumePostFlow && (
          <Card className="card-elevated" data-testid="resume-flow-steps">
            <CardContent className="pt-5 pb-4">
              <ProposalStepIndicator steps={RESUME_FLOW_STEPS} step={resumeFlowStepNum} />
            </CardContent>
          </Card>
        )}

        {/* Step: Add a Cover Letter? */}
        {showResumePostFlow && resumePostStep === 'cover_prompt' && (
          <Card className="card-elevated" data-testid="cover-letter-prompt-step">
            <CardHeader>
              <CardTitle className="text-lg">Add a Cover Letter?</CardTitle>
              <CardDescription>
                Optionally create a personalized cover letter using your resume. Skipping costs no extra credits.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="cover-job-title">Job title applying for</Label>
                  <Input
                    id="cover-job-title"
                    value={coverContext.jobTitle}
                    onChange={(e) => setCoverContext((c) => ({ ...c, jobTitle: e.target.value }))}
                    data-testid="cover-job-title"
                  />
                  {validationErrors.jobTitle && (
                    <p className="text-xs text-destructive">{validationErrors.jobTitle}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cover-company-name">Company name</Label>
                  <Input
                    id="cover-company-name"
                    value={coverContext.companyName}
                    onChange={(e) => setCoverContext((c) => ({ ...c, companyName: e.target.value }))}
                    data-testid="cover-company-name"
                  />
                  {validationErrors.companyName && (
                    <p className="text-xs text-destructive">{validationErrors.companyName}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cover-job-description">Job description</Label>
                <Textarea
                  id="cover-job-description"
                  rows={5}
                  placeholder="Paste the job posting or a short summary of requirements..."
                  value={coverContext.jobDescription}
                  onChange={(e) => setCoverContext((c) => ({ ...c, jobDescription: e.target.value }))}
                  data-testid="cover-job-description"
                />
                {validationErrors.jobDescription && (
                  <p className="text-xs text-destructive">{validationErrors.jobDescription}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Tone</Label>
                <div className="flex flex-wrap gap-4" data-testid="cover-tone-selector">
                  {COVER_LETTER_TONES.map((tone) => (
                    <label key={tone} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="cover-tone"
                        value={tone}
                        checked={coverContext.tone === tone}
                        onChange={() => setCoverContext((c) => ({ ...c, tone }))}
                      />
                      {tone}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSkipCoverLetter}
                  data-testid="skip-cover-letter-btn"
                >
                  Skip — just download my resume
                </Button>
                <Button
                  type="button"
                  onClick={handleContinueToCoverDraft}
                  className="glow-button"
                  data-testid="continue-cover-letter-btn"
                >
                  Continue to Cover Letter
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step: Cover Letter Draft */}
        {showResumePostFlow && resumePostStep === 'cover_draft' && (
          <Card className="card-elevated" data-testid="cover-letter-draft-step">
            <CardHeader>
              <CardTitle className="text-lg">Cover Letter Draft</CardTitle>
              <CardDescription>
                Generate a draft tailored to {coverContext.companyName || 'the role'}, then edit before finalizing.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 rounded-lg border bg-card/50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="text-sm font-medium">Cover letter body</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={fetchCoverLetterDraft}
                      disabled={coverDraftLoading || coverLetterGenerating}
                      data-testid="cover-letter-get-suggestions-btn"
                    >
                      {coverDraftLoading
                        ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Drafting…</>
                        : <><Sparkles className="mr-1 h-3 w-3" /> Get Suggestions</>}
                    </Button>
                    {coverDraft.trim() && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={fetchCoverLetterDraft}
                        disabled={coverDraftLoading || coverLetterGenerating}
                        data-testid="cover-letter-regenerate-btn"
                      >
                        Regenerate
                      </Button>
                    )}
                  </div>
                </div>
                <Textarea
                  rows={14}
                  value={coverDraft}
                  onChange={(e) => setCoverDraft(e.target.value)}
                  placeholder="Click Get Suggestions to generate your cover letter, then edit as needed."
                  data-testid="cover-letter-draft-textarea"
                />
                <p className="text-xs text-muted-foreground">
                  Click Get Suggestions to generate, then edit as needed before generating your PDF.
                </p>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setResumePostStep('cover_prompt')}
                  disabled={coverLetterGenerating}
                >
                  Back
                </Button>
                <div className="flex flex-col sm:items-end gap-1">
                  <Button
                    type="button"
                    onClick={handleGenerateCoverLetter}
                    disabled={coverLetterGenerating || !coverDraft.trim()}
                    className="glow-button"
                    data-testid="generate-cover-letter-btn"
                  >
                    {coverLetterGenerating
                      ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating cover letter…</>
                      : <>Generate Cover Letter — {coverLetterCredits} credits</>}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Resume credits already deducted. Cover letter adds {coverLetterCredits} credits.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Result Card — top banner for resume/package flows only (generic docs use bottom action bar) */}
        {showTopResultBanner && (
          <Card className="border-primary/30 bg-primary/5" data-testid="result-card" ref={resultCardRef}>
            <CardContent className="py-5">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-primary shrink-0" />
                  <div>
                    <p className="font-semibold text-foreground">
                      {isCoverLetterPage && packageResumeReady && packageCoverReady
                        ? 'Your application package is ready'
                        : isResume && isJobDownloadReady(coverLetterJobResult)
                          ? 'Your resume & cover letter is ready'
                          : isResume
                            ? 'Your resume is ready'
                            : `Your ${getDocTypeDisplayName(
                              isCoverLetterPage && packageCoverReady && !packageResumeReady
                                ? 'cover_letter'
                                : isCoverLetterPage && packageResumeReady && !packageCoverReady
                                  ? 'resume'
                                  : documentType,
                            ).toLowerCase()} is ready`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {isCoverLetterPage && packageResumeReady && packageCoverReady
                        ? 'Download your resume, cover letter, or both.'
                        : isResume && isJobDownloadReady(coverLetterJobResult)
                          ? 'Download your resume, cover letter, or both.'
                          : showTopDownloadActions
                            ? (() => {
                              const activeJob = isCoverLetterPage
                                ? (packageCoverReady ? coverLetterJobResult : resumeJobResult)
                                : jobResult;
                              const credits = isCoverLetterPage
                                ? (packageCoverReady ? coverLetterCredits : resumeCredits)
                                : docMeta.credits;
                              return formatResultCardMeta(activeJob, credits);
                            })()
                            : (() => {
                              const creditsLine = formatResultCardMeta(jobResult, docMeta.credits);
                              return creditsLine || 'Use the buttons below to download or regenerate your document.';
                            })()}
                    </p>
                  </div>
                </div>
                {showTopDownloadActions && (
                <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:justify-end">
                  {(showImmediateDownload || packageResumeReady) && (
                    <Button
                      onClick={isCoverLetterPage ? handleDownloadResumePackage : handleDownload}
                      data-testid="download-btn"
                      className="glow-button"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {primaryDownloadLabel}
                    </Button>
                  )}
                  {((isResume && coverLetterJobResult && isJobDownloadReady(coverLetterJobResult))
                    || packageCoverReady) && (
                    <>
                      <Button
                        variant={showImmediateDownload && isResume && coverLetterJobResult ? 'outline' : 'default'}
                        onClick={handleDownloadCoverLetter}
                        data-testid="download-cover-letter-btn"
                        className={!(showImmediateDownload || packageResumeReady) ? 'glow-button' : ''}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        {getDocumentDownloadLabel('cover_letter', coverLetterJobResult?.status, coverLetterCredits)}
                      </Button>
                      {(showImmediateDownload || (packageResumeReady && packageCoverReady)) && (
                        <Button
                          variant="secondary"
                          onClick={handleDownloadBoth}
                          data-testid="download-both-btn"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download Both
                        </Button>
                      )}
                    </>
                  )}
                </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {jobResult && jobResult.status === 'failed' && (
          <Card className="border-destructive/30 bg-destructive/5" data-testid="error-card">
            <CardContent className="py-4">
              <p className="font-medium text-destructive">Generation Failed</p>
              <p className="text-sm text-muted-foreground">{jobResult.error_message}</p>
            </CardContent>
          </Card>
        )}

        {/* Validation errors banner */}
        {(isResume || isCoverLetterPage) && Object.keys(validationErrors).length > 0 && (
          <div className="p-3 rounded-lg border border-destructive/30 bg-destructive/5 text-sm text-destructive space-y-1" data-testid="validation-errors">
            {Object.values(validationErrors).map((err, i) => <p key={i}>{err}</p>)}
          </div>
        )}

        {/* Cover letter package: Resume section */}
        {showCoverLetterPackageForm && (
          <>
            <Card className="card-elevated" data-testid="cover-package-resume-section" ref={resumeSectionRef}>
              <CardHeader>
                <CardTitle className="text-lg">Resume</CardTitle>
                <CardDescription>
                  Your resume data paired with this cover letter. Edit and regenerate, or download the existing PDF.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ResumeForm formData={resumePackageData} setFormData={setResumePackageData} api={api} />
                <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                  {hasCompletedResumePackage && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDownloadResumePackage}
                      data-testid="download-existing-resume-btn"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Existing Resume
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleScrollToResumeSection}
                    data-testid="edit-regenerate-resume-btn"
                  >
                    Edit &amp; Regenerate Resume
                  </Button>
                </div>
                {showResumeGenerateBtn && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t">
                    <p className="text-xs text-muted-foreground">
                      {resumeCredits} credits will be deducted when you generate.
                    </p>
                    <Button
                      type="button"
                      onClick={handleGenerateResumePackage}
                      disabled={resumeGenerating}
                      className="glow-button"
                      data-testid="generate-resume-package-btn"
                    >
                      {resumeGenerating
                        ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating resume…</>
                        : <>Generate Resume — {resumeCredits} credits</>}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Separator className="my-2" data-testid="resume-cover-letter-separator" />

            <Card className="card-elevated" data-testid="cover-package-cover-letter-section">
              <CardHeader>
                <CardTitle className="text-lg">Cover Letter</CardTitle>
                <CardDescription>
                  Job details and cover letter body for your application package.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="pkg-cover-job-title">Job title applying for</Label>
                    <Input
                      id="pkg-cover-job-title"
                      value={coverContext.jobTitle}
                      onChange={(e) => setCoverContext((c) => ({ ...c, jobTitle: e.target.value }))}
                      data-testid="cover-job-title"
                    />
                    {validationErrors.jobTitle && (
                      <p className="text-xs text-destructive">{validationErrors.jobTitle}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pkg-cover-company-name">Company name</Label>
                    <Input
                      id="pkg-cover-company-name"
                      value={coverContext.companyName}
                      onChange={(e) => setCoverContext((c) => ({ ...c, companyName: e.target.value }))}
                      data-testid="cover-company-name"
                    />
                    {validationErrors.companyName && (
                      <p className="text-xs text-destructive">{validationErrors.companyName}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pkg-cover-job-description">Job description</Label>
                  <Textarea
                    id="pkg-cover-job-description"
                    rows={5}
                    placeholder="Paste the job posting or a short summary of requirements..."
                    value={coverContext.jobDescription}
                    onChange={(e) => setCoverContext((c) => ({ ...c, jobDescription: e.target.value }))}
                    data-testid="cover-job-description"
                  />
                  {validationErrors.jobDescription && (
                    <p className="text-xs text-destructive">{validationErrors.jobDescription}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Tone</Label>
                  <div className="flex flex-wrap gap-4" data-testid="cover-tone-selector">
                    {COVER_LETTER_TONES.map((tone) => (
                      <label key={tone} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="cover-tone-package"
                          value={tone}
                          checked={coverContext.tone === tone}
                          onChange={() => setCoverContext((c) => ({ ...c, tone }))}
                        />
                        {tone}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-2 rounded-lg border bg-card/50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="text-sm font-medium">Cover letter body</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={fetchCoverLetterDraft}
                        disabled={coverDraftLoading || coverLetterGenerating}
                        data-testid="cover-letter-get-suggestions-btn"
                      >
                        {coverDraftLoading
                          ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Drafting…</>
                          : <><Sparkles className="mr-1 h-3 w-3" /> Get Suggestions</>}
                      </Button>
                      {coverDraft.trim() && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={fetchCoverLetterDraft}
                          disabled={coverDraftLoading || coverLetterGenerating}
                          data-testid="cover-letter-regenerate-btn"
                        >
                          Regenerate
                        </Button>
                      )}
                    </div>
                  </div>
                  <Textarea
                    rows={14}
                    value={coverDraft}
                    onChange={(e) => setCoverDraft(e.target.value)}
                    placeholder="Click Get Suggestions to generate your cover letter, then edit as needed."
                    data-testid="cover-letter-draft-textarea"
                  />
                </div>
                {showCoverGenerateBtn && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t">
                    <p className="text-xs text-muted-foreground">
                      {coverLetterCredits} credits will be deducted when you generate.
                    </p>
                    <Button
                      type="button"
                      onClick={handleGenerateCoverLetter}
                      disabled={coverLetterGenerating || !coverDraft.trim()}
                      className="glow-button"
                      data-testid="generate-cover-letter-btn"
                    >
                      {coverLetterGenerating
                        ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating cover letter…</>
                        : <>Generate Cover Letter — {coverLetterCredits} credits</>}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Document form — resume-specific or generic (not cover letter package) */}
        {showResumeForm ? (
          isResume ? (
            <ResumeForm formData={formData} setFormData={setFormData} api={api} />
          ) : (
            <GenericDocumentForm
              formData={formData} setFormData={setFormData} documentType={documentType}
              showCurrency={showCurrency} showLogo={showLogo}
              logoFile={logoFile} setLogoFile={setLogoFile}
              logoPreview={logoPreview} setLogoPreview={setLogoPreview}
              api={api}
            />
          )
        ) : null}

        {/* Generate Button — credits charged when generation succeeds (non-package doc types) */}
        {showResumeForm && !isCoverLetterPage && (
        <Card className="card-elevated" data-testid="generate-action-card">
          <CardContent className="pt-5 pb-5">
            {showImmediateDownload && !isResume && !generating && !polling && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5" data-testid="bottom-ready-banner">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 space-y-0.5">
                  <p className="text-sm font-medium text-foreground">
                    Your {getDocTypeDisplayName(documentType).toLowerCase()} is ready
                  </p>
                  {formatResultCardMeta(jobResult, docMeta.credits) && (
                    <p className="text-xs text-muted-foreground truncate">
                      {formatResultCardMeta(jobResult, docMeta.credits)}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {showImmediateDownload ? 'Regenerate or download' : 'Generate'}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    — {docMeta.credits} credits will be deducted when you generate.
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {showImmediateDownload
                    ? 'Edit the form above, then regenerate — or download your current PDF.'
                    : <>Your balance: <span className="font-medium text-primary">{user?.credit_balance || 0} credits</span></>}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                {showImmediateDownload && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDownload}
                    disabled={generating || polling}
                    data-testid="download-bottom-btn"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {primaryDownloadLabel}
                  </Button>
                )}
                <Button
                  onClick={handleGenerate}
                  disabled={generating || polling}
                  className="glow-button px-8"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {polling ? `Generating your ${docMeta.name.toLowerCase()}...` : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      {showImmediateDownload ? `Regenerate ${docMeta.name}` : `Generate ${docMeta.name}`}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </main>
    </div>
  );
}

